"use client"

import { ReactNode, useEffect, useRef, useState } from "react"

interface InViewMountProps {
  /** 자리를 잡아 둘 최소 높이 — 마운트 순간 스크롤이 튀지 않게 한다. */
  minHeight?: number
  rootMargin?: string
  children: ReactNode
}

/**
 * 화면에 들어올 때 처음 마운트한다.
 *
 * 탭 구조를 걷어내면서 전체 치료사례 목록이 한 페이지에 함께 놓이게 됐다.
 * 그대로 두면 상세를 열자마자 목록 조회가 나가 예전엔 없던 호출이 생긴다 —
 * 예전에 "탭을 눌렀을 때" 나가던 호출을 "그 자리까지 내려왔을 때"로 옮겨,
 * 화면에 닿지 않으면 조회하지 않는 성질을 그대로 지킨다.
 */
const InViewMount = ({ minHeight = 240, rootMargin = "200px", children }: InViewMountProps) => {
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const anchor = anchorRef.current
    if (!anchor || isVisible) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true)
      },
      { rootMargin },
    )
    observer.observe(anchor)
    return () => observer.disconnect()
  }, [isVisible, rootMargin])

  if (isVisible) return <>{children}</>

  return <div ref={anchorRef} style={{ minHeight }} aria-hidden="true" />
}

export default InViewMount
